import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CommandLifecycle,
  EnvelopeV2,
  glt,
  HandlerBuilder,
  initializeQrAndKey,
  LifecycleEnabledTopic,
  LifecyclePayloadForTopic,
  PayloadForTopic,
  QrVersion,
  webApp,
} from '@coinweb/webapp-library';
import { v4 } from 'uuid';

export const LOCAL_STORAGE_KEY = 'privKeyInitializeWebappLibrary';

export interface BaseHandlers<TTopic extends LifecycleEnabledTopic = LifecycleEnabledTopic> {
  onLifecycle: <TStage extends CommandLifecycle>(
    lifecycle: TStage,
    payload: LifecyclePayloadForTopic<TTopic, TStage>,
    ctx: EnvelopeV2
  ) => void;
  onReconciliationPubKey?: (key: string) => void;
  onError?: (error: unknown) => void;
}

interface WebappCommandConfig<TPayload, THandlers, TTopic = LifecycleEnabledTopic> {
  topic: TTopic;
  buildInitialRequest: (
    correlationId: string,
    payload: TPayload
  ) => {
    id: string;
    topic: TTopic;
    payload: TPayload;
  };
  compress?: boolean;
  setupSubscription?: (builder: HandlerBuilder, handlers: THandlers) => void;
}

export const useWebappCommandLifecycleCore = <
  TPayload,
  TTopic extends LifecycleEnabledTopic = LifecycleEnabledTopic,
  THandlers extends BaseHandlers<TTopic> = BaseHandlers<TTopic>,
>(
  config: WebappCommandConfig<TPayload, THandlers, TTopic>
) => {
  const [isExecuting, setIsExecuting] = useState(false);

  const subscriptionRef = useRef<(() => void) | undefined>(undefined);
  const correlationIdRef = useRef<string>(v4());
  const reconciliationPubKeyRef = useRef<string | null>(null);

  const cancelPendingRequest = useCallback(async () => {
    try {
      if (reconciliationPubKeyRef.current) {
        await webApp.reply_to_pubkey(reconciliationPubKeyRef.current, {
          topic: glt(config.topic, CommandLifecycle.Cancelled),
          correlationId: correlationIdRef.current,
          payload: {} as PayloadForTopic<`${TTopic}.cancelled`>,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      void subscriptionRef.current?.();
      subscriptionRef.current = undefined;
      reconciliationPubKeyRef.current = null;
      setIsExecuting(false);
    }
  }, [config.topic]);

  useEffect(() => {
    return () => {
      void subscriptionRef.current?.();
      subscriptionRef.current = undefined;
      reconciliationPubKeyRef.current = null;
      setIsExecuting(false);
    };
  }, []);

  const executeCommand = useCallback(
    (commandPayload: TPayload, handlers: THandlers): string | null => {
      if (isExecuting) return null;

      // Generate new correlation ID for every execution attempt
      correlationIdRef.current = v4();

      const existingKey = localStorage.getItem(LOCAL_STORAGE_KEY);

      try {
        setIsExecuting(true);

        const {
          privKey: subPrivKey,
          qr,
          subscription,
        } = initializeQrAndKey<TPayload>(existingKey ?? undefined, QrVersion.V2, {
          initialRequest: config.buildInitialRequest(correlationIdRef.current, commandPayload),
          compress: Boolean(config.compress),
        });

        const builder = webApp
          .on(subPrivKey, subscription)
          .handleCommand(config.topic, correlationIdRef.current, (commandBuilder) => {
            commandBuilder
              .handleLifecycle(CommandLifecycle.Initiated, (payload, ctx) => {
                const reconKey = payload?.reconciliationPubKey;
                if (reconKey) {
                  reconciliationPubKeyRef.current = reconKey;
                  handlers.onReconciliationPubKey?.(reconKey);
                }
                handlers.onLifecycle(CommandLifecycle.Initiated, payload, ctx);
              })
              .handleLifecycle(CommandLifecycle.Updated, (payload, ctx) => {
                handlers.onLifecycle(CommandLifecycle.Updated, payload, ctx);
              })
              .handleLifecycle(CommandLifecycle.Pending, (payload, ctx) => {
                handlers.onLifecycle(CommandLifecycle.Pending, payload, ctx);
              })
              .handleLifecycle(CommandLifecycle.Confirmed, (payload, ctx) => {
                handlers.onLifecycle(CommandLifecycle.Confirmed, payload, ctx);
                setIsExecuting(false);
              })
              .handleLifecycle(CommandLifecycle.Cancelled, (payload, ctx) => {
                handlers.onLifecycle(CommandLifecycle.Cancelled, payload, ctx);
                setIsExecuting(false);
              })
              .handleLifecycle(CommandLifecycle.Failed, (payload, ctx) => {
                handlers.onLifecycle(CommandLifecycle.Failed, payload, ctx);
                setIsExecuting(false);
              });
          });

        // Allow additional subscription setup (e.g., for legacy handlers)
        config.setupSubscription?.(builder, handlers);

        subscriptionRef.current = builder;

        return qr;
      } catch (error) {
        console.error(error);
        setIsExecuting(false);
        handlers.onError?.(error);
        return null;
      }
    },
    [isExecuting, config]
  );

  return {
    // actions
    executeCommand,
    cancelPendingRequest,
    // state
    isExecuting,
  };
};
